import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import type { Area, Category } from '@ilteducation/content-ref-data-types';
import { getAllRefData } from '@/data-provider/refDataProvider';
import type { MaterialV1 } from '@ilteducation/content-registry-types';
import { areasOfCategories, categoryColorLookup, filterCategories } from '@/foundations/helpers/refDataFilters';
import RefDataAutocomplete from '@/components/material/RefDataAutocomplete.vue';

type Props = {
  refdataLinks?: MaterialV1['refdataLinks'];
  realms?: string[];
  subscriptionTypes?: string[];
}
type Emits = {
  (e: 'change', refdataLinks: MaterialV1['refdataLinks']): void;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'RefDataLinks',
  props: {
    refdataLinks: null,
    realms: null,
    subscriptionTypes: null
  },
  emits: ["change"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'change', refdataLinks: MaterialV1['refdataLinks']): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  refdataLinks?: MaterialV1['refdataLinks'];
  realms?: string[];
  subscriptionTypes?: string[];
}





const availableCategories = ref<Category[]>([]);
const availableAreas = ref<Area[]>([]);

Promise.all([getAllRefData('categories'), getAllRefData('areas')]).then(([categories, areas]) => {
  availableCategories.value = categories;
  availableAreas.value = areas;
});

const categoryColor = computed(() => categoryColorLookup(availableCategories.value));
const showAreas = computed(() => props.subscriptionTypes?.some((sub) => sub.includes('immigrant')) ?? false);

const selectedCategoryIds = ref<string[]>([]);
const selectedAreaIds = ref<string[]>([]);

const offeredCategories = computed(() =>
  filterCategories(availableCategories.value, {
    excludeKinds: ['grade'],
    realms: props.realms,
    subscriptionTypes: props.subscriptionTypes,
    selectedIds: selectedCategoryIds.value,
  }),
);

// Areas inherit realm/subscription from their parent category, so they are filtered through
// the categories that survived.
const offeredAreas = computed(() =>
  areasOfCategories(
    availableAreas.value,
    filterCategories(availableCategories.value, {
      realms: props.realms,
      subscriptionTypes: props.subscriptionTypes,
    }),
    selectedAreaIds.value,
  ),
);

watch(
  () => props.refdataLinks,
  (links) => {
    selectedCategoryIds.value = (links ?? []).filter((link) => link.type === 'Category').map((link) => link.id);
    selectedAreaIds.value = (links ?? []).filter((link) => link.type === 'Area').map((link) => link.id);
  },
  { immediate: true },
);

const emitChanges = () => {
  emit('change', [
    ...selectedCategoryIds.value.map((id) => ({ id, type: 'Category' as const })),
    ...selectedAreaIds.value.map((id) => ({ id, type: 'Area' as const })),
  ]);
};

const onCategories = (ids: string[]) => {
  selectedCategoryIds.value = ids;
  emitChanges();
};

const onAreas = (ids: string[]) => {
  selectedAreaIds.value = ids;
  emitChanges();
};

const areaDisplayName = (area: Area | Record<string, unknown>): string => {
  const category = availableCategories.value.find((c) => c.id === (area as Area).categoryId);
  const name = String((area as Area).name ?? '');
  return category ? `${name} (${category.category})` : name;
};

return { __sfc: true,props, emit, availableCategories, availableAreas, categoryColor, showAreas, selectedCategoryIds, selectedAreaIds, offeredCategories, offeredAreas, emitChanges, onCategories, onAreas, areaDisplayName, RefDataAutocomplete }
}

})